import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import SuperAdminDashboardChartService from "../../../api/Dashboard/SuperAdminDashboardChart/SuperAdminDashboardChartService";
import { Grid } from "@mui/material";

// Define types for API response
interface ChartData {
  period: string;
  value: number;
}

const SubscriptionLineGraph = () => {
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [timePeriod, setTimePeriod] = useState<string>("yearly"); // Default period
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Function to format and fill missing data
  const formatAndFillData = (apiData: any[], period: string): ChartData[] => {
    if (period === "weekly") {
      const fullWeek = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const today = new Date();

      // Generate the last 7 days dynamically
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(today.getDate() - (6 - i)); // Get last 7 days in order
        return {
          period: fullWeek[date.getDay()], // Only store weekday name
          dateKey: date.toISOString().split("T")[0], // Store ISO date separately
          value: 0,
        };
      });

      // Map API data by date
      const apiDataMap = new Map(
        apiData.map((item: any) => [item.date, item.count ?? 0])
      );

      // Fill missing data
      return last7Days.map((day) => ({
        period: day.period, // Only the weekday name
        value: apiDataMap.get(day.dateKey) || 0, // Match using the stored date
      }));
    }

    // Default case for monthly and yearly
    return apiData.map((item: any) => ({
      period: item.date, // Keep original format for other periods
      value: item.count ?? 0,
    }));
  };

  // Function to fetch data
  const fetchSubscriptionChartDetails = async (period: string) => {
    const apiPeriodMap: Record<string, string> = {
      weekly: "week",
      monthly: "month",
      yearly: "year",
    };

    setLoading(true);
    setError(null);

    try {
      const res = await SuperAdminDashboardChartService(apiPeriodMap[period]);
      const resData = res.data;
      if (Array.isArray(resData)) {
        setChartData(formatAndFillData(resData, period));
      } else {
        setChartData([]);
        setError("Unexpected API response format.");
      }
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      console.error("❌ Error fetching subscription data:", err);
      setChartData([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchSubscriptionChartDetails(timePeriod);
  }, [timePeriod]);

  return (
    <>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6} padding={3}>
          <div className="bg-white shadow-lg rounded-lg p-5 h-[60vh] max-w-3xl mx-auto">
            {/* Time Period Selection Buttons */}
            <h1 className="text-center font-satoshi font-extrabold mb-6 bg-orange-50 text-[#027aae] rounded-md">
              Active Users
            </h1>
            <div className="flex justify-center gap-4 mb-6">
              {["weekly", "monthly", "yearly"].map((period) => (
                <button
                  key={period}
                  onClick={() => setTimePeriod(period)}
                  className={`px-4 py-2 text-sm font-semibold transition-all rounded-lg ${
                    timePeriod === period
                      ? "bg-[#e59411] text-white shadow-md font-satoshi"
                      : "bg-blue-200 text-blue-900 hover:bg-blue-300 font-satoshi"
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>

            {/* Loading, Error, or Data */}
            {loading ? (
              <p className="text-blue-500 text-center font-medium">
                Loading data...
              </p>
            ) : error ? (
              <p className="text-red-500 text-center font-medium">{error}</p>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={"70%"}>
                <LineChart
                  data={chartData}
                  margin={{ top: 25, right: 60, left: 20, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="period"
                    tick={{
                      fill: "#FFA500",
                      fontFamily: "satoshi",
                      fontSize: "12px",
                    }}
                  />
                  <YAxis
                    tick={{ fill: "#00BFFF", fontFamily: "satoshi" }}
                    label={{
                      value: "Number of Users", // Title for the Y-axis
                      angle: -90, // Rotate vertically
                      position: "insideLeft", // Position it inside on the left
                      style: {
                        textAnchor: "middle",
                        fill: "#027aae",
                        fontSize: "14px",
                      },
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      borderRadius: 8,
                      color: "#027aae",
                      fontFamily: "satoshi",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FFA500"
                    strokeWidth={3}
                    dot={{
                      r: 5,
                      fill: "#027aae",
                      strokeWidth: 3,
                      stroke: "#e59411",
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500 text-center font-medium">
                No data available
              </p>
            )}
          </div>
        </Grid>
    </>
  );
};

export default SubscriptionLineGraph;
